import type {MediaTypeLoadingStates, MediaTypePublishingParams, PostState, SocialMediaAccountId, SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {asyncnoop, noop} from '@Utils/general.util';
import type {RefObject} from 'react';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import type {AppDispatch} from '@/store';
import type {BatchMetricsKickoffResponse, MetricsSnapshot} from '@Components/social-media/social-media-metrics-refresh.types';
import {updatePostAfterMetricsRefresh} from '@Panels/social-posts-dialog-panel/social-posts-dialog-panel.reducer';
import type {SocialMediaGridRequestThunkParams} from '@Components/social-media/social-media-thunk';
import {
  doPublishForAccount,
  getPostableSocialMediaAccountsForUser,
  getSocialMediaAccountsForUser,
  getSocialMediaPostsForUser,
  updateSocialMediaPostPublishingParams,
} from '@Components/social-media/social-media-thunk';
import {
  DEFAULT_POSTS_LIMIT,
  markPostsAsRefreshing,
  mergeRefreshedPosts,
  resetPosts,
  unmarkPostsAsRefreshing,
  updateAccountSorter,
  updateDoRefetchGridForNewSorting,
  updatePostSorter,
} from '@Components/social-media/social-media-reducer';
import type {AccountState, SocialAccount} from '@Components/social-media/account.vo';
import type {
  AccountStateFilter,
  ContainerQueryParams,
  DesignSelection,
  PostStateFilter,
  PublishedUrlMapForAccount,
  RetryStatusForMediaType,
  SocialPlatform,
  SocialPostGridContainerQuery,
} from '@Components/social-media/social-media.types';
import {AccountSortingMetric, PostSortingMetric, socialGridContainerQuery, SocialPostEvent} from '@Components/social-media/social-media.types';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {getNonStoryPlatformMediaType, getSocialPlatformName, isEventOneClickPromotionFlow, isEventsSocialPostingFlow, isPostVisibleForFilter} from '@Libraries/social-media';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {executeThunk} from '@Utils/thunk.util';
import type {ErrorResponseUpdatePublishingParams} from '@Components/social-media-wizard/social-media-wizard.types';
import {ErrorUpdatePublishingParams, SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import {RetryStatus, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {LoadingStates} from '@Utils/loading.util';
import {
  setErrorBanner,
  unsetRetryingStatusForAccountWithMediaType,
  updatePublishedAccountPostUrlMapForAccountWithMediaType,
  updatePublishErrorMessageForAccount,
  updatePublishErrorMessageForAllParams,
  updatePublishingStatusForAccountWithMediaType,
  updatePublishingStatusForAllAccountMediaTypes,
  updateSavingPublishingParamsState,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {useDesignsToPublish} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {StoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {haveAllPublishingStatesLoaded} from '@Libraries/social-media-wizard-library';

export const useSocialMediaPosts = (): SocialPost[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.posts;
  });
};

export const useSocialMediaHaveAccountsLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.haveAccountsLoaded;
  });
};

export const useSocialMediaHavePostsLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.havePostsLoaded;
  });
};

export const useDoRefetchGridForNewSorting = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.doRefetchGridForNewSorting;
  });
};

export const useGetSharedInTeamPosts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.getSharedInTeamPosts;
  });
};

export const useSocialMediaPostsOffset = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.posts.length;
  });
};

export const useAccountStateFilter = (): AccountStateFilter => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountStateFilter;
  });
};

export const useIsAccountVisibleForFilter = (accountState: AccountState): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountStateFilter[accountState];
  });
};

export const useSocialPostGridContainerQuery = (): ContainerQueryParams => {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, socialGridContainerQuery);

  const containerQueryParamProps: SocialPostGridContainerQuery = {
    xsmall: params.xsmall,
    small: params.small,
    medium: params.medium,
    large: params.large,
    xlarge: params.xlarge,
    xxlarge: params.xxlarge,
  };

  return {
    containerQueryParamProps,
    containerRef,
    hasContainerQueryInitialized: params.small !== undefined,
  };
};

export const useIsPostVisibleForFilter = (postState: PostState): boolean => {
  const postStateFilter = useAppSelector((state) => {
    return state.socialMediaGrid.postStateFilter;
  });

  const [isVisible, setIsVisible] = useState(true);

  useDidUpdateEffect(() => {
    setIsVisible(isPostVisibleForFilter(postState, postStateFilter));
  }, [postStateFilter]);

  return isVisible;
};

export const usePostStateFilter = (): PostStateFilter => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.postStateFilter;
  });
};

export const useSearchTerm = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.searchTerm;
  });
};

export const usePostSorter = (): PostSortingMetric => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.postSorter;
  });
};

export const useMorePostsOnServer = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.morePostsOnServer;
  });
};

export const useGetDeletedPosts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.getDeletedPosts;
  });
};
export const useAccountSorter = (): AccountSortingMetric => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountSorter;
  });
};

export const useSocialMediaAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts;
  });
};

export const useLoadSocialAccountsForWizard = (): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    void executeThunk(() => {
      return dispatch(getPostableSocialMediaAccountsForUser());
    }).then(noop);
  }, []);
};

export const usePublishingStatusForAllAccounts = (): Record<SocialMediaAccountId, MediaTypeLoadingStates> => {
  return useAppSelector((state): Record<SocialMediaAccountId, MediaTypeLoadingStates> => {
    return state.socialMediaWizard.publishingStatusForMediaTypes;
  });
};

export const usePublishingStatusForAccountWithMediaType = (idSocialMediaAccount: SocialMediaAccountId, mediaType: PlatformMediaType): LoadingStates => {
  return useAppSelector((state) => {
    if (state.socialMediaWizard.publishingStatusForMediaTypes[idSocialMediaAccount] === undefined) {
      return LoadingStates.LOADING;
    }

    return state.socialMediaWizard.publishingStatusForMediaTypes[idSocialMediaAccount][mediaType];
  });
};

export const useRetryStatusForAccount = (idSocialMediaAccount: SocialMediaAccountId): RetryStatusForMediaType => {
  return useAppSelector((state): RetryStatusForMediaType => {
    return state.socialMediaWizard.retryPublishingMapForAccount[idSocialMediaAccount];
  });
};

export const useHaveAllPostsPublished = (): boolean => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParams = usePublishingParams();

  return haveAllPublishingStatesLoaded(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams);
};

export const useRetryPublishingPostOrReelForAccount = (idSocialMediaAccount: SocialMediaAccountId): void => {
  const dispatch = useAppDispatch();
  const retryStatusForAccount = useRetryStatusForAccount(idSocialMediaAccount);

  if (!retryStatusForAccount) {
    console.error('No retry status for account with id ', idSocialMediaAccount, ' found');
    return;
  }

  const post = useSocialMediaWizardPost();
  const publishingParams = usePublishingParams()[idSocialMediaAccount];

  if (!publishingParams) {
    console.error('No publishing params found for account with id: ', idSocialMediaAccount);
  }

  const platformMediaType = getNonStoryPlatformMediaType(publishingParams);

  if (!platformMediaType) {
    return;
  }

  const wizardAccounts = useSocialMediaWizardAccounts();
  const foundAccount = wizardAccounts.find((account) => {
    return account.id === idSocialMediaAccount;
  });
  const platform = foundAccount?.type;

  const onErrorRequest = (resetRetryStatusOnCompletion: () => void): void => {
    dispatch(
      updatePublishingStatusForAccountWithMediaType({
        idSocialMediaAccount,
        publishingStatus: LoadingStates.NOT_LOADED,
        platformMediaType,
      })
    );

    resetRetryStatusOnCompletion();
  };

  const resetRetryStatusOnCompletion = (): void => {
    dispatch(unsetRetryingStatusForAccountWithMediaType({accountId: idSocialMediaAccount, platformMediaType}));
  };

  const onRetrySuccessResponse = (data: object): void => {
    if (data && 'postUrl' in data) {
      dispatch(
        updatePublishedAccountPostUrlMapForAccountWithMediaType({
          accountId: idSocialMediaAccount,
          mediaType: platformMediaType,
          url: data.postUrl as string,
        })
      );
    }

    if (data && 'publishErrorMessage' in data) {
      const errorMessage: string | null = typeof data.publishErrorMessage === 'string' ? data.publishErrorMessage : null;
      dispatch(
        updatePublishErrorMessageForAccount({
          idSocialMediaAccount,
          publishErrorMessage: errorMessage,
          platformMediaType,
        })
      );

      if (errorMessage) {
        onErrorRequest(resetRetryStatusOnCompletion);
        return;
      }
    }

    if (platform) {
      const platformString = getSocialPlatformName(platform);
      trackPublishingGA4Events(GA4EventName.POST_PUBLISHED, {[GA4EventParamName.PLATFORM]: platformString});

      if (isPromotingEvent()) {
        trackEventPromotionPublishedForPlatform(platformString);
      }
    }

    dispatch(
      updatePublishingStatusForAccountWithMediaType({
        idSocialMediaAccount,
        publishingStatus: LoadingStates.LOADED,
        platformMediaType,
      })
    );
    resetRetryStatusOnCompletion();
  };

  useEffect(() => {
    const retryStatusForStories = retryStatusForAccount[platformMediaType];
    if (!retryStatusForStories) {
      return;
    }

    if (retryStatusForStories !== RetryStatus.RETRYING) {
      return;
    }

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            isRetry: true,
            mediaType: platformMediaType,
          })
        );
      },
      (data: object) => {
        onRetrySuccessResponse(data);
      },
      () => {
        onErrorRequest(resetRetryStatusOnCompletion);
      }
    );
  }, [retryStatusForAccount]);
};

export const useRetryPublishingStoryForAccount = (idSocialMediaAccount: SocialMediaAccountId): void => {
  const dispatch = useAppDispatch();
  const retryStatusForAccount = useRetryStatusForAccount(idSocialMediaAccount);

  if (!retryStatusForAccount) {
    console.error('No retry status for account with id ', idSocialMediaAccount, ' found');
    return;
  }

  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();
  const foundAccount = wizardAccounts.find((account) => {
    return account.id === idSocialMediaAccount;
  });
  const platform = foundAccount?.type;

  const onErrorRequest = (resetRetryStatusOnCompletion: () => void): void => {
    dispatch(
      updatePublishingStatusForAccountWithMediaType({
        idSocialMediaAccount,
        publishingStatus: LoadingStates.NOT_LOADED,
        platformMediaType: PlatformMediaType.STORIES,
      })
    );

    resetRetryStatusOnCompletion();
  };

  useEffect(() => {
    const retryStatusForStories = retryStatusForAccount[PlatformMediaType.STORIES];
    if (!retryStatusForStories) {
      return;
    }

    if (retryStatusForStories !== RetryStatus.RETRYING) {
      return;
    }

    const resetRetryStatusOnCompletion = (): void => {
      dispatch(
        unsetRetryingStatusForAccountWithMediaType({
          accountId: idSocialMediaAccount,
          platformMediaType: PlatformMediaType.STORIES,
        })
      );
    };

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType: PlatformMediaType.STORIES,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            isRetry: true,
            mediaType: PlatformMediaType.STORIES,
          })
        );
      },
      (data: object) => {
        if (data && 'postUrl' in data) {
          dispatch(
            updatePublishedAccountPostUrlMapForAccountWithMediaType({
              accountId: idSocialMediaAccount,
              mediaType: PlatformMediaType.STORIES,
              url: data.postUrl as string,
            })
          );
        }

        if (data && 'publishErrorMessage' in data) {
          const errorMessage: string | null = typeof data.publishErrorMessage === 'string' ? data.publishErrorMessage : null;
          dispatch(
            updatePublishErrorMessageForAccount({
              idSocialMediaAccount,
              publishErrorMessage: errorMessage,
              platformMediaType: PlatformMediaType.STORIES,
            })
          );

          if (errorMessage) {
            onErrorRequest(resetRetryStatusOnCompletion);
            return;
          }
        }

        if (platform) {
          const platformString = getSocialPlatformName(platform);
          const platformNameForGA = `${platformString}_${PlatformMediaType.STORIES}`;
          trackPublishingGA4Events(GA4EventName.POST_PUBLISHED, {[GA4EventParamName.PLATFORM]: platformNameForGA});

          if (isPromotingEvent()) {
            trackEventPromotionPublishedForPlatform(platformString);
          }
        }

        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADED,
            platformMediaType: PlatformMediaType.STORIES,
          })
        );
        resetRetryStatusOnCompletion();
      },
      () => {
        onErrorRequest(resetRetryStatusOnCompletion);
      }
    );
  }, [retryStatusForAccount]);
};

// Note : Reel and Post can be coupled under the same hook because only either of them will be published in ALL cases.
// i.e - There will be no case that a Reel and Post can co-exist and be published together (as opposed to Stories)
export const useStartPublishingReelOrPostForAccount = (idSocialMediaAccount: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  const dispatch = useAppDispatch();
  const savingPublishingParamsState = useSavingPublishingParamsState();
  const publishingAction = useSocialPostPublishingAction();
  const publishingParams = usePublishingParams();

  if (platformMediaType === PlatformMediaType.STORIES) {
    return;
  }

  if (!publishingParams[idSocialMediaAccount][platformMediaType]) {
    console.error('No Publishing Params found for account with id: ', idSocialMediaAccount, ' and platform media type: ', platformMediaType);
    return;
  }

  const publishingState = usePublishingStatusForAccountWithMediaType(idSocialMediaAccount, platformMediaType);
  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();

  const foundAccount = wizardAccounts.find((account) => {
    return account.id === idSocialMediaAccount;
  });

  const platform = foundAccount?.type;

  const onErrorRequest = (): void => {
    dispatch(
      updatePublishedAccountPostUrlMapForAccountWithMediaType({
        accountId: idSocialMediaAccount,
        mediaType: platformMediaType,
        url: '',
      })
    );

    dispatch(
      updatePublishingStatusForAccountWithMediaType({
        idSocialMediaAccount,
        publishingStatus: LoadingStates.NOT_LOADED,
        platformMediaType,
      })
    );
  };

  useEffect(() => {
    if (publishingState === LoadingStates.LOADED || publishingState === LoadingStates.NOT_LOADED) {
      return;
    }

    if (savingPublishingParamsState !== LoadingStates.LOADED) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            mediaType: platformMediaType,
            isRetry: false,
          })
        );
      },
      (data: object) => {
        if (data && 'postUrl' in data) {
          dispatch(
            updatePublishedAccountPostUrlMapForAccountWithMediaType({
              accountId: idSocialMediaAccount,
              mediaType: platformMediaType,
              url: data.postUrl as string,
            })
          );
        }

        if (data && 'publishErrorMessage' in data) {
          const errorMessage: string | null = typeof data.publishErrorMessage === 'string' ? data.publishErrorMessage : null;
          dispatch(
            updatePublishErrorMessageForAccount({
              idSocialMediaAccount,
              publishErrorMessage: errorMessage,
              platformMediaType,
            })
          );

          if (errorMessage) {
            onErrorRequest();
            return;
          }
        }

        if (platform) {
          const platformString = getSocialPlatformName(platform);
          trackPublishingGA4Events(GA4EventName.POST_PUBLISHED, {[GA4EventParamName.PLATFORM]: platformString});

          if (isPromotingEvent()) {
            trackEventPromotionPublishedForPlatform(platformString);
          }
        }

        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADED,
            platformMediaType,
          })
        );
      },
      () => {
        onErrorRequest();
      }
    );
  }, [savingPublishingParamsState]);
};

export const useStartPublishingStoryForAccount = (idSocialMediaAccount: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  const dispatch = useAppDispatch();
  const savingPublishingParamsState = useSavingPublishingParamsState();
  const publishingAction = useSocialPostPublishingAction();
  const publishingState = usePublishingStatusForAccountWithMediaType(idSocialMediaAccount, PlatformMediaType.STORIES);
  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();

  if (platformMediaType !== PlatformMediaType.STORIES) {
    return;
  }

  const foundAccount = wizardAccounts.find((account) => {
    return account.id === idSocialMediaAccount;
  });

  const platform = foundAccount?.type;

  const onErrorRequest = (): void => {
    dispatch(
      updatePublishedAccountPostUrlMapForAccountWithMediaType({
        accountId: idSocialMediaAccount,
        mediaType: PlatformMediaType.STORIES,
        url: '',
      })
    );

    dispatch(
      updatePublishingStatusForAccountWithMediaType({
        idSocialMediaAccount,
        publishingStatus: LoadingStates.NOT_LOADED,
        platformMediaType: PlatformMediaType.STORIES,
      })
    );
  };

  useEffect(() => {
    if (publishingState === LoadingStates.LOADED || publishingState === LoadingStates.NOT_LOADED) {
      return;
    }

    if (savingPublishingParamsState !== LoadingStates.LOADED) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType: PlatformMediaType.STORIES,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            mediaType: PlatformMediaType.STORIES,
            isRetry: false,
          })
        );
      },
      (data: object) => {
        if (data && 'postUrl' in data) {
          dispatch(
            updatePublishedAccountPostUrlMapForAccountWithMediaType({
              accountId: idSocialMediaAccount,
              mediaType: PlatformMediaType.STORIES,
              url: data.postUrl as string,
            })
          );
        }

        if (data && 'publishErrorMessage' in data) {
          const errorMessage: string | null = typeof data.publishErrorMessage === 'string' ? data.publishErrorMessage : null;
          dispatch(
            updatePublishErrorMessageForAccount({
              idSocialMediaAccount,
              publishErrorMessage: errorMessage,
              platformMediaType: PlatformMediaType.STORIES,
            })
          );

          if (errorMessage) {
            onErrorRequest();
            return;
          }
        }

        if (platform) {
          const platformString = getSocialPlatformName(platform);
          const platformNameForGA = `${platformString}_${PlatformMediaType.STORIES}`;
          trackPublishingGA4Events(GA4EventName.POST_PUBLISHED, {[GA4EventParamName.PLATFORM]: platformNameForGA});

          if (isPromotingEvent()) {
            trackEventPromotionPublishedForPlatform(platformString);
          }
        }

        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADED,
            platformMediaType: PlatformMediaType.STORIES,
          })
        );
      },
      () => {
        onErrorRequest();
      }
    );
  }, [savingPublishingParamsState]);
};

export const useUpdatePublishingParams = (): void => {
  const dispatch = useAppDispatch();
  const post = useSocialMediaWizardPost();
  const publishingAction = useSocialPostPublishingAction();
  const step = useWizardStep();
  const designs = useDesignsToPublish();
  const publishingParams = usePublishingParams();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  const updatePublishingStatusForSaveOrSchedule = (loadingState: LoadingStates): void => {
    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      dispatch(updatePublishingStatusForAllAccountMediaTypes(loadingState));
    }
  };

  const fireMultiPlatformPublishEvent = (): void => {
    if (!post || publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    const numPlatforms = accountIdsToPublishTo.length;
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_NUMPLATFORMS, {[GA4EventParamName.NUM_PLATFORMS]: `${numPlatforms}`});
  };

  const fireScheduleTrackingEvent = (): void => {
    if (!post) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.SCHEDULE) {
      return;
    }

    const {accounts} = post;

    accounts.forEach((item) => {
      const platform = item.type;
      const platformName = getSocialPlatformName(platform);

      const doesStoryExistForAccount = publishingParams[item.id].STORIES !== null;

      if (doesStoryExistForAccount) {
        const storyPlatformForGA = `${platformName}_${PlatformMediaType.STORIES}`;
        trackPublishingGA4Events(GA4EventName.POST_SCHEDULED, {[GA4EventParamName.PLATFORM]: storyPlatformForGA});

        if (isPromotingEvent()) {
          trackEventPromotionScheduledForPlatform(platformName);
        }
      }

      trackPublishingGA4Events(GA4EventName.POST_SCHEDULED, {[GA4EventParamName.PLATFORM]: platformName});
      if (isPromotingEvent()) {
        trackEventPromotionScheduledForPlatform(platformName);
      }
    });
  };
  const getSanitizedTimeZoneOffset = (): null | number => {
    let timeZoneOffset = null;
    if (post && post.timeZoneOffset !== null && post.timeZoneOffset !== undefined) {
      timeZoneOffset = post.timeZoneOffset;
    }
    return timeZoneOffset;
  };

  useEffect(() => {
    if (step === SocialMediaWizardStep.PUBLISH) {
      void executeThunk(
        () => {
          updatePublishingStatusForSaveOrSchedule(LoadingStates.LOADING);
          dispatch(updateSavingPublishingParamsState(LoadingStates.LOADING));
          const publishOn: number | undefined = post ? post.publishOn : undefined;
          const timeZoneOffset: number | null = getSanitizedTimeZoneOffset();

          return dispatch(
            updateSocialMediaPostPublishingParams({
              idPost: post ? post.id : null,
              designs: designs || null,
              publishOn: publishOn ?? null,
              timeZoneOffset,
              publishingParams,
            })
          );
        },
        () => {
          fireScheduleTrackingEvent();
          fireMultiPlatformPublishEvent();
          updatePublishingStatusForSaveOrSchedule(LoadingStates.LOADED);
          dispatch(updateSavingPublishingParamsState(LoadingStates.LOADED));
        },
        (response: ErrorResponseUpdatePublishingParams) => {
          const {error, data} = response;
          if (error.code === ErrorUpdatePublishingParams.RATE_LIMITED && error.message !== '' && accountIdsToPublishTo.length > 0) {
            dispatch(updatePublishErrorMessageForAllParams(error.message));
          }

          if (error.code === ErrorUpdatePublishingParams.ALREADY_PUBLISHED) {
            return;
          }

          if (error.code === ErrorUpdatePublishingParams.SOCIAL_ACCOUNTS_NEED_REAUTH && data?.unhealthyAccounts) {
            dispatch(setErrorBanner({type: 'reauth-required', accounts: data.unhealthyAccounts}));
          }

          fireMultiPlatformPublishEvent();
          updatePublishingStatusForSaveOrSchedule(LoadingStates.NOT_LOADED);
          dispatch(updateSavingPublishingParamsState(LoadingStates.NOT_LOADED));
        }
      );
    }
  }, [step]);
};

export const useSocialMediaWizardPost = (): SocialPost | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.post;
  });
};

export const useSocialPostPublishingAction = (): SocialPostPublishingAction | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingAction;
  });
};

export const useSocialMediaWizardAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accounts;
  });
};

export const useAccountsToPublishTo = (): SocialAccount[] => {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  return accounts.filter((account) => accountIdsToPublishTo.includes(account.id));
};

export const useHasCommonCaption = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasCommonCaption;
  });
};

export const useHasCommonPostCover = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasCommonPostCover;
  });
};

export const useCommonCaption = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.commonCaption;
  });
};

export const useTriggerNext = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.triggerNext;
  });
};

export const useTriggerPrevious = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.triggerPrevious;
  });
};

export const useAccountIdsToPublishTo = (): number[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountIdsToPublishTo;
  });
};

export const usePostPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    if (!state.socialMediaWizard.publishingParams[socialMediaAccountId]) {
      return null;
    }

    return state.socialMediaWizard.publishingParams[socialMediaAccountId].POST;
  });
};

export const useReelPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId].REELS;
  });
};

export const useStoryPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId].STORIES;
  });
};

export const useAllPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): MediaTypePublishingParams => {
  return useAppSelector((state): MediaTypePublishingParams => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId];
  });
};

export const useStoryGuidelineForAccount = (accountId: number): StoryGuideline => {
  return useAppSelector((state): StoryGuideline => {
    return state.socialMediaWizard.storyGuidelineForAccount[accountId];
  });
};

export const usePublishingParams = (): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams;
  });
};

export const useDesignToResize = (): DesignSelection | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.designToResize;
  });
};

export const useAccountTypeToConnect = (): SocialPlatform | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountTypeToConnect;
  });
};

export const useNextButtonIcon = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonIcon;
  });
};

export const useNextButtonText = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonText;
  });
};

export const useEmphasizedPlatform = (): SocialPlatform | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.emphasizedPlatform;
  });
};

export const useNextButtonDisabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonDisabled;
  });
};

export const useSavingPublishingParamsState = (): LoadingStates => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.savingPublishingParamsState;
  });
};

export const useWizardStep = (): SocialMediaWizardStep => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.wizardStep;
  });
};

export const useLoadSocialAccounts = (): void => {
  const haveAccountsLoaded = useSocialMediaHaveAccountsLoaded();
  const dispatch = useAppDispatch();
  const isUserLogged = isUserLoggedIn();

  useEffect(() => {
    if (haveAccountsLoaded || !isUserLogged) {
      return;
    }

    void executeThunk(() => {
      return dispatch(getSocialMediaAccountsForUser());
    }).then(noop);
  }, [isUserLogged]);
};

export const useReloadGridForSorterIfNeeded = (): void => {
  const sortPostsBy = usePostSorter();
  const morePostsOnServer = useMorePostsOnServer();
  const numPostsLoaded = useSocialMediaPostsOffset();
  const getSharedInTeam = useGetSharedInTeamPosts();
  const dispatch = useAppDispatch();

  useDidUpdateEffect(() => {
    if (!morePostsOnServer) {
      return;
    }

    dispatch(updateDoRefetchGridForNewSorting(true));
    dispatch(resetPosts());
    const limit = numPostsLoaded;
    const offset = 0;
    const getDeleted = false;

    const args: SocialMediaGridRequestThunkParams = {
      limit,
      offset,
      getDeleted,
      sortPostsBy,
      getSharedInTeam,
    };

    void executeThunk(() => {
      return dispatch(getSocialMediaPostsForUser(args));
    }).then();
  }, [sortPostsBy]);
};
export const useLoadSocialPosts = (getDeleted: boolean, getSharedInTeam: boolean): void => {
  const dispatch = useAppDispatch();
  const offset = useSocialMediaPostsOffset();
  const doRefetchGridForNewSorting = useDoRefetchGridForNewSorting();
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const sortPostsBy = usePostSorter();
  const limit = DEFAULT_POSTS_LIMIT;

  const getPostsFromServer = async (): Promise<void> => {
    const args: SocialMediaGridRequestThunkParams = {
      limit,
      offset,
      getDeleted,
      sortPostsBy,
      getSharedInTeam,
    };

    await executeThunk(() => {
      return dispatch(getSocialMediaPostsForUser(args));
    });
  };

  useEffect(() => {
    if (havePostsLoaded || doRefetchGridForNewSorting) {
      return;
    }

    void getPostsFromServer().then(noop);
  }, [havePostsLoaded]);
};

export const useLazyLoadPostsFromServer = (lazyloadFunction: VoidFunction): void => {
  const DEBOUNCE_DELAY = 20;
  const morePostsOnServer = useMorePostsOnServer();

  useEffect(() => {
    window.addEventListener('scroll', doLazyloadIfNeeded, {passive: true});

    return (): void => {
      window.removeEventListener('scroll', doLazyloadIfNeeded);
    };
  }, [morePostsOnServer]);

  const doLazyloadIfNeeded = window.PMW.debounce(() => {
    if (!morePostsOnServer) {
      return;
    }

    const tar = document.getElementById('js-social-posts-grid');

    if (tar) {
      const bottom = tar.getBoundingClientRect().bottom <= window.innerHeight;
      if (bottom) {
        lazyloadFunction();
      }
    }
  }, DEBOUNCE_DELAY) as VoidFunction;
};

const AUTO_METRICS_POLL_TIMEOUT_MS = 4 * 60 * 1000;

export const useIsPostRefreshing = (postId: string): boolean => {
  return useAppSelector((state) => state.socialMediaGrid.refreshingPostIds[postId] ?? false);
};

/**
 * Auto-refreshes metrics for the grid's stale published posts. Each ID is kicked
 * off at most once per mount (lazy-loading refreshes only the new posts) via one
 * kickoff + one long-poll loop over the whole batch, cancelled on unmount. Each
 * post clears its skeleton the moment it settles — nothing ready is held back.
 * Premium-only.
 *
 * `refreshablePostIds` arrive in the grid's render order, so the backend enqueues
 * the stale posts top-first (a best-effort nudge toward above-the-fold settling
 * sooner). They're the posts the grid already determined are stale, so we
 * optimistically show their skeleton BEFORE paint (a layout effect) — the grid
 * never flashes stale numbers and then a skeleton. After the kickoff, posts the
 * server didn't enqueue revert to their numbers.
 */
export const useAutoRefreshMetricsForVisiblePosts = (refreshablePostIds: string[]): void => {
  const dispatch = useAppDispatch();
  const posts = useSocialMediaPosts();
  const postsRef = useLatest(posts);
  const dialogPostId = useAppSelector((state) => state.socialPostDialog.post?.id ?? null);
  const dialogPostIdRef = useLatest(dialogPostId);
  const kickedOffRef = useRef<Set<string>>(new Set());
  const activeEventIdsRef = useRef<Set<string>>(new Set());
  const eventIdCounterRef = useRef(0);
  const abortedRef = useRef(false);
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const prevHavePostsLoadedRef = useRef(havePostsLoaded);

  useEffect(() => {
    // Full-grid reset (sort/team/trash toggle) empties posts and flips havePostsLoaded false: forget kicked-off IDs so the reloaded page is re-evaluated. Lazy-load pagination also flips havePostsLoaded false but keeps the loaded posts, so the empty-posts guard skips it — already-kicked-off rows aren't re-kicked. Backend guard still dedupes within the freshness window.
    if (prevHavePostsLoadedRef.current && !havePostsLoaded && postsRef.current.length === 0) {
      kickedOffRef.current.clear();
    }
    prevHavePostsLoadedRef.current = havePostsLoaded;
  }, [havePostsLoaded]);

  useLayoutEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const newPostIds = refreshablePostIds.filter((id) => !kickedOffRef.current.has(id));
    if (newPostIds.length > 0) {
      dispatch(markPostsAsRefreshing(newPostIds));
    }
  }, [refreshablePostIds]);

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const newPostIds = refreshablePostIds.filter((id) => !kickedOffRef.current.has(id));
    if (newPostIds.length === 0) {
      return;
    }

    newPostIds.forEach((id) => kickedOffRef.current.add(id));
    eventIdCounterRef.current += 1;
    const eventId = `social-metrics-refresh-${eventIdCounterRef.current}`;
    activeEventIdsRef.current.add(eventId);

    void runAutoMetricsRefreshBatch(newPostIds, eventId, dispatch, postsRef, dialogPostIdRef, abortedRef).finally(() => {
      activeEventIdsRef.current.delete(eventId);
    });
  }, [refreshablePostIds]);

  useEffect(() => {
    return (): void => {
      abortedRef.current = true;
      activeEventIdsRef.current.forEach((eventId) => {
        document.dispatchEvent(new Event(window.PMW.getPollingStopEvent(eventId) as string));
      });
    };
  }, []);
};

const runAutoMetricsRefreshBatch = async (
  postIds: string[],
  eventId: string,
  dispatch: AppDispatch,
  postsRef: RefObject<SocialPost[]>,
  dialogPostIdRef: RefObject<string | null>,
  abortedRef: RefObject<boolean>
): Promise<void> => {
  // postIds were already marked refreshing (skeleton shown) by the hook's layout effect.
  try {
    const kickoff = (await window.PMW.writeLocal('socialmedia/requestAutoMetricsRefresh', {postIds})) as BatchMetricsKickoffResponse;
    const enqueuedPostIds = kickoff?.enqueuedPostIds ?? [];

    // Posts we optimistically skeleton'd but the server didn't enqueue (already
    // fresh / terminal / rate-limited) revert to their numbers right away.
    const notEnqueuedPostIds = postIds.filter((id) => !enqueuedPostIds.includes(id));
    if (notEnqueuedPostIds.length > 0) {
      dispatch(unmarkPostsAsRefreshing(notEnqueuedPostIds));
    }

    // One long-poll over all enqueued posts: each resolve returns the posts
    // settled so far, which we merge + clear the skeleton on immediately, then
    // re-poll the rest. Every post surfaces the moment it settles (nothing ready
    // is ever held back) over a single connection; narrowing the pending set
    // keeps this a handful of sequential requests, not one per row.
    let pendingPostIds = enqueuedPostIds;
    const deadlineMs = performance.now() + AUTO_METRICS_POLL_TIMEOUT_MS;
    while (pendingPostIds.length > 0 && !abortedRef.current) {
      const remainingMs = deadlineMs - performance.now();
      if (remainingMs <= 0) {
        break;
      }

      const wavePostIds = pendingPostIds;
      // eslint-disable-next-line no-await-in-loop -- waves are sequential: each poll narrows the pending set
      const wave = (await window.PMW.pollingAjaxCallAsync(
        asyncnoop,
        () => window.PMW.readLocal('socialmedia/pollBatchMetricsStatus', {postIds: wavePostIds, since: kickoff.since}),
        remainingMs,
        eventId
      )) as MetricsSnapshot[] | undefined;

      if (!wave || wave.length === 0) {
        break;
      }

      applyRefreshedSnapshots(wave, dispatch, postsRef.current, dialogPostIdRef.current);
      const settledPostIds = new Set(wave.map((snapshot) => snapshot.postId));
      dispatch(unmarkPostsAsRefreshing(Array.from(settledPostIds)));
      pendingPostIds = pendingPostIds.filter((id) => !settledPostIds.has(id));
    }
  } catch (e) {
    window.PMW.log(`Auto metrics refresh failed: ${String(e)}`);
  } finally {
    // Backstop: clear any skeleton still showing for this batch (timeout / error).
    dispatch(unmarkPostsAsRefreshing(postIds));
  }
};

const applyRefreshedSnapshots = (snapshots: MetricsSnapshot[], dispatch: AppDispatch, posts: SocialPost[], dialogPostId: string | null): void => {
  const postsById = new Map(posts.map((post) => [post.id, post]));
  const refreshedPosts: SocialPost[] = [];

  snapshots.forEach((snapshot) => {
    const existing = postsById.get(snapshot.postId);
    if (existing) {
      refreshedPosts.push(mergeSnapshotIntoPost(existing, snapshot));
    }
  });

  if (refreshedPosts.length === 0) {
    return;
  }

  dispatch(mergeRefreshedPosts(refreshedPosts));

  refreshedPosts.forEach((post) => {
    window.dispatchEvent(new CustomEvent(SocialPostEvent.UPDATED, {detail: {postId: post.id, post}}));
    if (post.id === dialogPostId) {
      dispatch(updatePostAfterMetricsRefresh(post));
    }
  });
};

/**
 * Replaces a post's numbers only when the snapshot's timestamp advanced past
 * the post's — otherwise just updates job state, so a terminal/legacy snapshot
 * can't wipe good numbers.
 */
const mergeSnapshotIntoPost = (post: SocialPost, snapshot: MetricsSnapshot): SocialPost => {
  const merged: SocialPost = {
    ...post,
    metricsJobState: snapshot.metricsJobState ?? post.metricsJobState,
  };

  const hasFreshMetrics =
    snapshot.metrics !== null &&
    snapshot.metricsUpdatedOn !== null &&
    (post.metricsUpdatedOn == null || snapshot.metricsUpdatedOn > post.metricsUpdatedOn);

  if (hasFreshMetrics) {
    merged.metrics = snapshot.metrics ?? post.metrics;
    merged.metricsUpdatedOn = snapshot.metricsUpdatedOn;
  }

  return merged;
};

export const useAccountsSorterListItems = (): ListItemStorage[] => {
  const dispatch = useAppDispatch();

  const accountSorter = useAccountSorter();
  return [
    {
      id: AccountSortingMetric.DATE_CREATED as string,
      isSelected: accountSorter === AccountSortingMetric.DATE_CREATED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_date_added'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (AccountSortingMetric.DATE_CREATED as string)) {
          dispatch(updateAccountSorter(AccountSortingMetric.DATE_CREATED));
        }
      },
    },
    {
      id: AccountSortingMetric.BY_PLATFORM as string,
      isSelected: accountSorter === AccountSortingMetric.BY_PLATFORM,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_platform'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (AccountSortingMetric.BY_PLATFORM as string)) {
          dispatch(updateAccountSorter(AccountSortingMetric.BY_PLATFORM));
        }
      },
    },
  ];
};
export const usePostsSorterListItems = (): ListItemStorage[] => {
  const dispatch = useAppDispatch();

  const postSorter = usePostSorter();
  return [
    {
      id: PostSortingMetric.LAST_MODIFIED as string,
      isSelected: postSorter === PostSortingMetric.LAST_MODIFIED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_last_modified'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (PostSortingMetric.LAST_MODIFIED as string)) {
          dispatch(updatePostSorter(PostSortingMetric.LAST_MODIFIED));
        }
      },
    },
    {
      id: PostSortingMetric.DATE_PUBLISHED as string,
      isSelected: postSorter === PostSortingMetric.DATE_PUBLISHED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_publishing_date'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (PostSortingMetric.DATE_PUBLISHED as string)) {
          dispatch(updatePostSorter(PostSortingMetric.DATE_PUBLISHED));
        }
      },
    },
  ];
};

export const useAccountResizeCompletedStatusForPosters = (account: SocialAccount | undefined, platformMediaType: PlatformMediaType): AccountResizeStatus => {
  return useAppSelector((state): AccountResizeStatus => {
    if (!account) {
      return AccountResizeStatus.NOT_RESIZED;
    }

    const resizingStatusForDesigns = state.socialMediaWizard.accountResizeStatusMapForMediaType[account.id][platformMediaType];

    if (!resizingStatusForDesigns) {
      return AccountResizeStatus.NOT_RESIZED;
    }

    for (const resizePageMap of Object.values(resizingStatusForDesigns)) {
      for (const resizeInformation of Object.values(resizePageMap)) {
        if (resizeInformation !== AccountResizeStatus.RESIZE_DONE) {
          return resizeInformation;
        }
      }
    }

    return AccountResizeStatus.RESIZE_DONE;
  });
};

export const usePublishedAccountUrlMap = (): PublishedUrlMapForAccount => {
  return useAppSelector((state): PublishedUrlMapForAccount => {
    return state.socialMediaWizard.publishedAccountUrlMapForMediaType;
  });
};

export const useHasAccounts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts.length > 0;
  });
};

export const isPromotingEvent = (): boolean => {
  return isEventOneClickPromotionFlow() || isEventsSocialPostingFlow();
};

export const trackEventPromotionPublishedForPlatform = (platformName: string): void => {
  trackPublishingGA4Events(GA4EventName.EVENTS_PROMOTE_SOCIAL_PUBLISH, {[GA4EventParamName.PLATFORM]: platformName});
};

export const trackEventPromotionScheduledForPlatform = (platformName: string): void => {
  trackPublishingGA4Events(GA4EventName.EVENTS_PROMOTE_SOCIAL_SCHEDULE, {[GA4EventParamName.PLATFORM]: platformName});
};
